/* eslint-disable import/no-unused-modules */
import { useRef, useEffect } from 'react';
import type { PrimitiveAtom } from 'jotai';
import { useAtom } from 'jotai';

/**
 * Custom hook that manages atom-based event notifications
 *
 * This hook creates a notification system using Jotai atoms. It tracks changes
 * to a numeric atom and triggers a callback when the value increases.
 *
 * @param atom - Jotai atom that holds a numeric counter for event notifications
 * @param onEvent - Callback function to execute when the atom value increases
 * @returns Object containing a trigger function to increment the atom value
 *
 * @example
 * ```tsx
 * const { trigger } = useAtomEvent(someAtom, () => {
 *   console.log('Event triggered!');
 * });
 *
 * // Later, call trigger() to increment the atom and fire the callback
 * trigger();
 * ```
 */
export const useAtomEvent = (
    atom: PrimitiveAtom<number>,
    onEvent?: () => void
) => {
    const [eventNotification, setEventNotification] = useAtom(atom);
    const lastNotificationRef = useRef(eventNotification);
    useEffect(() => {
        if (eventNotification > lastNotificationRef.current) {
            lastNotificationRef.current = eventNotification;
            onEvent?.();
        }
    }, [eventNotification, onEvent]);
    const trigger = () => {
        setEventNotification((prev) => prev + 1);
    };
    return {
        trigger,
    };
};
